import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import * as yup from 'yup';

import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import ConfirmationModal from 'components/global/ConfirmationModal';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import DeliveryDetails from 'pages/ImportOrders/DeliveryDetails/DeliveryDetails';
import OrderDetails from 'pages/Shipments/Shipments/OrderForm/OrderDetails/OrderDetails';
import OrderPickUpLocation from 'pages/Shipments/Shipments/OrderForm/OrderPickUpLocation/OrderPickUpLocation';

import { IMPORT } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import PATHS, { mergePaths } from 'utils/paths';

import { faAngleLeft, faAngleRight } from '@fortawesome/pro-solid-svg-icons';

export const AddImportOrderModal = NiceModal.create(({ mode, modalName, setOrder }) => {
	// Hooks and states declaration
	const { t } = useTranslation();
	const { closeModal } = useModalRouter();
	const modal = useModal();
	const axios = useAxiosPrivate();
	const { showError } = useAlert();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const user = useSelector((state) => state.user);
	const warehouseCheck = useSelector((state) => state.instruction?.warehouseCheck);
	const pickupLocationVisible = useMemo(() => !warehouseCheck, []);
	const [isMinimizing, setIsMinimizing] = useState(false);
	const [addPickupLocationLoading, setAddPickupLocationLoading] = useState(false);
	const [tab, setTab] = useState(0);
	const orderPickUpLocationRef = useRef();
	const defaultBox = useMemo(
		() =>
			user.defaultBox
				? {
						ID: user.defaultBox.id,
						width: user.defaultBox.width,
						height: user.defaultBox.height,
						length: user.defaultBox.length,
						name: user.defaultBox.boxName,
						weight: 1
					}
				: { width: 10, height: 10, length: 10, weight: 1 },
		[user]
	);

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	};

	const schema = useMemo(
		() => [
			yup.object().shape({
				firstName: yup.string().nullable().required(),
				email: yup.string().email().nullable(),
				phone: yup.string().nullable().required().phoneNumber(),
				warehouseID: yup.number().nullable().required(),
				warehouseAddress: yup.string().nullable().required(),
				warehouseCountry: yup.string().nullable().required()
			}),
			yup.object().shape({
				orderID: yup
					.string()
					.nullable()
					.matches(/^[a-zA-Z0-9-]+$/, t('errorMsg.onlyEnglishLetters'))
					.min(3)
					.trim()
					.max(50)
					.required(),
				orderDate: yup.string().nullable().required(),
				orderComments: yup.string().nullable(),
				products: yup.array().of(
					yup.object().shape({
						productID: yup.number().nullable(),
						name: yup.string().nullable().required(),
						quantity: yup.number().min(1).nullable(),
						price: yup.number().min(0).nullable(),
						tax: yup.number().nullable(),
						total: yup.number().nullable(),
						SKU: yup.string().nullable().required(),
						hsCode: yup.string().nullable().required()
					})
				),
				grandTotal: yup.number().nullable().min(1).required(),
				currency: yup
					.string()
					.nullable()
					.required()
					.when(['products'], ([products], schema) =>
						schema.test({
							test: (value) => products?.every((product) => product.currency === value),
							message: t('errorMsg.orderAndProductsCurrencyMismatch')
						})
					),
				warehouseID: yup.number().nullable(),
				brandID: yup
					.number()
					.nullable()
					.when([], {
						is: () => user.userType === 'BrandManager',
						then: (schema) => schema.required()
					}),
				weight: yup.number().min(0.01).nullable().required(),
				packageCount: yup.number().min(1).nullable().required(),
				boxes: yup.array().of(
					yup.object().shape({
						ID: yup.number().nullable(),
						width: yup.number().positive().nullable().required(),
						height: yup.number().positive().nullable().required(),
						length: yup.number().positive().nullable().required(),
						weight: yup.number().positive().nullable().required()
					})
				)
			})
		],
		[t]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema[tab]),
		defaultValues: {
			// Customer Details
			firstName: null,
			email: null,
			phone: null,
			warehouseID: null,
			warehouseAddress: null,
			warehouseCountry: null,

			// Order Details
			orderID: null,
			orderDate: dayjs().toISOString(),
			orderComments: null,
			brandID: null,
			paymentMethod: 'paid',
			currency: 'CNY',
			grandTotal: null,
			weight: 1,
			packageCount: 1,
			manuallyAdded: true,
			products: [
				{
					productID: null,
					name: null,
					hsCode: null,
					SKU: null,
					quantity: 1,
					price: null,
					tax: null,
					total: null,
					currency: 'CNY'
				}
			],
			boxes: [defaultBox],
			isInternationalOrder: true
		}
	});

	const {
		handleSubmit,
		setValue,
		trigger,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const onSubmit = async (data) => {
		await axios
			.post(IMPORT.ADD_IMPORT_ORDER, {
				// Customer Details
				firstName: data.firstName,
				email: data.email,
				phone: data.phone,
				warehouseID: data.warehouseID,
				originCountry: 'CN',
				// Order Details
				orderID: data.orderID,
				orderDate: data.orderDate,
				orderComments: data.orderComments,
				brandID: data.brandID,
				grandTotal: Number(data.grandTotal),
				currency: data.currency,
				items: data.products.map((product) => ({
					total: product.total,
					tax: Number(product.tax),
					price: product.price,
					quantity: product.quantity,
					productID: product.productID,
					SKU: product.SKU,
					name: product.name,
					hsCode: product.hsCode
				})),
				packageCount: Number(data.packageCount),
				weight: Number(data.weight),
				boxes: data.boxes
			})
			.then((response) => {
				const { status, data: responseData } = response;
				if (status === 200) {
					showSuccessModal(responseData.ID);
					setOrder?.(responseData.ID);
					dispatch(updateDynamicListsInPage());
					handleCloseModal();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
				AppLogger.error(error);
			});
	};

	const showSuccessModal = useCallback((orderId) => {
		NiceModal.show(ConfirmationModal, {
			lottieName: 'success',
			title: `addImportOrderSuccess.title`,
			subTitle: `addImportOrderSuccess.subtitle`,
			rejectLabel: 'addOrderSuccess.rejectLabel',
			confirmLabel: `addImportOrderSuccess.confirmLabel`,
			yesAction: () => {
				navigate(mergePaths(PATHS.IMPORT_ORDERS, PATHS.IMPORT_CALCULATOR), {
					state: {
						orderID: orderId,
						external: true,
						resetState: true
					}
				});
			}
		});
	}, []);

	const handleContinue = async () => {
		const canContinue = await trigger(
			['firstName', 'email', 'phone', 'address', 'warehouseID', 'warehouseAddress', 'warehouseCountry'],
			{ shouldFocus: true }
		);

		if (canContinue) {
			nextStep();
		}
	};

	const nextStep = () => {
		if (tab < 2) {
			setTab(tab + 1);
		}
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const stages = useMemo(
		() => [
			...(pickupLocationVisible ? [{ name: 'pickupLocation' }] : []),
			{ name: 'deliveryDetails' },
			{ name: 'orderDetails' }
		],
		[pickupLocationVisible]
	);

	return (
		<Modal
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label="addOrder"
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}
			fullwidth
			maxWidth="xx-large"
			name={modalName}
			className="modal-addImportOrderModal">
			<ModalTitle>
				<Typography variant="p1">addImportOrder</Typography>
				<ProgressWizard stages={stages} setIndex={setTab} index={tab} gutterBottom />
			</ModalTitle>

			<ModalContent style={{ width: '100%' }}>
				{pickupLocationVisible && tab === 0 && (
					<OrderPickUpLocation
						ref={orderPickUpLocationRef}
						nextStep={nextStep}
						setWarehouseID={(ID) => {
							setValue('warehouseID', ID);
						}}
					/>
				)}

				{(!pickupLocationVisible || tab > 0) && (
					<Form borderless formMethods={formMethods}>
						{(pickupLocationVisible ? tab === 1 : tab === 0) && <DeliveryDetails formMode="add" />}
						{(pickupLocationVisible ? tab === 2 : tab === 1) && (
							<OrderDetails formMode="add" orderType="import" />
						)}
					</Form>
				)}
			</ModalContent>

			<ModalActions>
				<Button
					label="cancel"
					variant="blackOutline"
					onClick={handleCloseModal}
					css={css`
						text-decoration: underline;
						border: none !important;
					`}
				/>
				{tab !== 0 && !(tab === 1 && pickupLocationVisible === true) && (
					<Button label="previous" variant="outline" icon={faAngleLeft} onClick={() => prevStep()} />
				)}
				{(tab === 0 && pickupLocationVisible === false) || (tab === 1 && pickupLocationVisible === true) ? (
					<Button
						icon={faAngleRight}
						label="continue"
						iconPos="right"
						onClick={handleContinue}
						variant="blue"
						loading={isLoading}
					/>
				) : null}
				{tab === 0 && pickupLocationVisible === true && (
					<Button
						icon={faAngleRight}
						label="continue"
						iconPos="right"
						onClick={async () => {
							setAddPickupLocationLoading(true);
							await orderPickUpLocationRef.current?.save();
							setAddPickupLocationLoading(false);
						}}
						variant="blue"
						loading={addPickupLocationLoading}
					/>
				)}
				{(tab === 2 || (tab === 1 && pickupLocationVisible === false)) && (
					<Button
						lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
						className="addButton"
						label="add"
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
						disabled={!isDirty && mode !== 'duplicate'}
						disabledTooltip={!isDirty && mode !== 'duplicate' && 'formInfo.noChanges'}
						variant="blue"
						loading={isLoading}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default AddImportOrderModal;
